import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Message,
  Divider,
  Header,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";

const PupilDataModal = ({ isOpen, onClose, onConfirm, numberOfPupils }) => {
  const { t } = useTranslation();
  const [pupils, setPupils] = useState([]);
  const [errors, setErrors] = useState({});

  // Initialize pupils array when modal opens or numberOfPupils changes
  useEffect(() => {
    if (isOpen) {
      const initialPupils = Array.from(
        { length: numberOfPupils },
        (_, index) => ({
          nome: "",
          cognome: "",
          annoNascita: "",
        })
      );
      setPupils(initialPupils);
      setErrors({});
    }
  }, [isOpen, numberOfPupils]);

  const handleInputChange = (index, field, value) => {
    const updatedPupils = [...pupils];
    updatedPupils[index][field] = value;
    setPupils(updatedPupils);

    // Clear error for this field
    const errorKey = `${index}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    pupils.forEach((pupil, index) => {
      // Validate first name
      if (!pupil.nome.trim()) {
        newErrors[`${index}-nome`] = "Il nome è obbligatorio";
      }

      // Validate last name
      if (!pupil.cognome.trim()) {
        newErrors[`${index}-cognome`] = "Il cognome è obbligatorio";
      }

      // Validate year of birth
      if (!pupil.annoNascita) {
        newErrors[`${index}-annoNascita`] = "L'anno di nascita è obbligatorio";
      } else {
        const year = parseInt(pupil.annoNascita, 10);
        if (isNaN(year) || year < 1900 || year > currentYear) {
          newErrors[
            `${index}-annoNascita`
          ] = `Inserire un anno tra 1900 e ${currentYear}`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(pupils);
    }
  };

  const handleCancel = () => {
    setPupils([]);
    setErrors({});
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleCancel} size="small">
      <Modal.Header>{t("inserisci_dati_allievi")}</Modal.Header>
      <Modal.Content scrolling>
        <Form>
          {pupils.map((pupil, index) => (
            <div key={index}>
              <Header
                as="h3"
                color="blue"
                style={{
                  marginTop: index === 0 ? "0" : "20px",
                  marginBottom: "15px",
                }}
              >
                {t("allievo")} {index + 1}
              </Header>
              <Form.Field error={!!errors[`${index}-nome`]}>
                <label>{t("nome")}</label>
                <input
                  placeholder={t("nome")}
                  value={pupil.nome}
                  onChange={(e) =>
                    handleInputChange(index, "nome", e.target.value)
                  }
                />
                {errors[`${index}-nome`] && (
                  <Message negative size="small">
                    {errors[`${index}-nome`]}
                  </Message>
                )}
              </Form.Field>

              <Form.Field error={!!errors[`${index}-cognome`]}>
                <label>{t("cognome")}</label>
                <input
                  placeholder={t("cognome")}
                  value={pupil.cognome}
                  onChange={(e) =>
                    handleInputChange(index, "cognome", e.target.value)
                  }
                />
                {errors[`${index}-cognome`] && (
                  <Message negative size="small">
                    {errors[`${index}-cognome`]}
                  </Message>
                )}
              </Form.Field>

              <Form.Field error={!!errors[`${index}-annoNascita`]}>
                <label>{t("anno_nascita")}</label>
                <input
                  type="number"
                  placeholder="YYYY"
                  value={pupil.annoNascita}
                  onChange={(e) =>
                    handleInputChange(index, "annoNascita", e.target.value)
                  }
                  min="1900"
                  max={new Date().getFullYear()}
                />
                {errors[`${index}-annoNascita`] && (
                  <Message negative size="small">
                    {errors[`${index}-annoNascita`]}
                  </Message>
                )}
              </Form.Field>

              {index < pupils.length - 1 && <Divider />}
            </div>
          ))}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleCancel}>{t("annulla")}</Button>
        <Button primary onClick={handleConfirm}>
          {t("conferma")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default PupilDataModal;
